import { getSiteName } from '@provision/formatting-utils';
import { useEffect, useState } from 'react';
import { TabLabelRows } from 'src/components/GlobalTabs/tab-labels/tab-label-rows';
import { useAddressRecord } from 'src/graphql';
import { useGlobalStore } from 'src/store';
import { SiteTabEntity, TabType, useGlobalTabs } from 'src/utils/hooks';

type SiteLabelProps = SiteTabEntity['entity'];

type SiteLabelState = SiteLabelProps & { partnerName: string };

// SiteLabel receives preserved information about the tab and shows it as an initial view while loading fresh
// data from the server and replacing it. It guarantees that users don't need to wait for data from the server to
// interact with tabs.
export const SiteLabel = (props: SiteLabelProps) => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { address: selectedSite, isLoading } = useAddressRecord({
    addressId: props.addressId,
  });

  const [state, setState] = useState<SiteLabelState>({
    ...props,
    partnerName: '',
  });

  const { activeTab, closeTab, findTab } = useGlobalTabs();
  const isActive = activeTab?.id === props.addressId && activeTab?.type === TabType.Site;

  useEffect(() => {
    if (!isLoading && selectedSite) {
      const { partnerId, partner, addressId } = selectedSite;
      setState({
        id: addressId,
        partnerName: partner.businessName,
        partnerId,
        addressId,
      });
    }

    // Close inactive tab if there's no site with specified Id. It is important to avoid
    // closing active tab as it is handled by SiteExpanded component at the moment when
    // site doesn't exist anymore, but inactive tabs aren't linked to SiteExpanded.
    if (!isLoading && !selectedSite && !isActive) {
      const tab = findTab(props.addressId, TabType.Site);
      if (tab) {
        closeTab(tab);
      }
    }
  }, [isActive, findTab, isLoading, props.addressId, closeTab, selectedSite]);

  const { partnerName } = state;

  const siteName = selectedSite ? getSiteName(selectedSite) : undefined;

  // A site has no friendly id, so the reference row carries the partner alone.
  return (
    <TabLabelRows
      tooltip={siteName}
      overline={selectedPartnerId ? undefined : partnerName}
      title={siteName}
    />
  );
};
